// src/components/Companies.tsx
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Company, CompanyLocation } from '../types/CompanyTypes';
import { MapContext } from '../contexts/MapContext';
import { useAuth } from '../contexts/AuthContext';
import { referralsService } from '../services/referrals.service';
import MultiSelect from './MultiSelect';
import ComparisonButton from './ComparisonButton';
import './Companies.css';
import './Companies.mobile.css';
import './UnifiedSidebar.css';

interface CompaniesProps {
  companies: Company[];
  darkMode: boolean;
  onCompanySelect?: (company: Company) => void;
  onViewReferralDashboard?: (companyId: string, companyName: string) => void;
  initialFilters?: {
    searchTerm?: string;
    countries?: string[];
  };
}

const Companies: React.FC<CompaniesProps> = ({ companies, darkMode, onCompanySelect, onViewReferralDashboard, initialFilters }) => {
  // Access map context for highlighting locations
  const mapContext = useContext(MapContext);
  const { isAuthenticated, user } = useAuth();
  
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState<string>(initialFilters?.searchTerm || '');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(initialFilters?.countries || []);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [topEmployerFilter, setTopEmployerFilter] = useState<string>('all');
  
  // State for collapsible sections
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState<boolean>(false);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState<boolean>(false);
  
  // State for referrals
  const [referrals, setReferrals] = useState<Set<string>>(new Set());
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  
  // Refs for scroll detection
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const scrollDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isStatsCollapsedRef = useRef(isStatsCollapsed);
  const isFiltersCollapsedRef = useRef(isFiltersCollapsed);
  
  // Keep refs in sync with state
  useEffect(() => {
    isStatsCollapsedRef.current = isStatsCollapsed;
  }, [isStatsCollapsed]);
  
  useEffect(() => {
    isFiltersCollapsedRef.current = isFiltersCollapsed;
  }, [isFiltersCollapsed]);
  
  // Update filters when initialFilters prop changes
  useEffect(() => {
    if (initialFilters?.searchTerm !== undefined) {
      setSearchTerm(initialFilters.searchTerm);
    }
    if (initialFilters?.countries !== undefined) {
      setSelectedCountries(initialFilters.countries);
    }
  }, [initialFilters]);
  
  // Load user's referrals when component mounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadUserReferrals();
    }
  }, [isAuthenticated, user?.id]);
  
  const loadUserReferrals = async () => {
    if (!user?.id) return;
    
    setIsLoadingReferrals(true);
    try {
      const referredCompanies = await referralsService.getReferredCompanies(user.id);
      setReferrals(referredCompanies);
    } catch (error) {
      // console.error('Error loading referrals:', error);
    } finally {
      setIsLoadingReferrals(false);
    }
  };
  
  // Auto-collapse insights and filters on scroll down
  useEffect(() => {
    let scrollElement: HTMLElement | null = null;
    let lastScrollTop = 0;
    
    const handleScroll = () => {
      if (!scrollElement) return;
      const scrollTop = scrollElement.scrollTop;
      
      // Only proceed if we've scrolled down more than threshold
      if (scrollTop > 30 && scrollTop > lastScrollTop) {
        // Collapse both insights and filters sections
        if (!isStatsCollapsedRef.current) {
          setIsStatsCollapsed(true);
          isStatsCollapsedRef.current = true;
        }
        
        if (!isFiltersCollapsedRef.current) {
          setIsFiltersCollapsed(true);
          isFiltersCollapsedRef.current = true;
        }
      }
      
      // Update the scroll position
      lastScrollTop = scrollTop;
    };

    // Only monitor the main unified-sidebar-content
    const timer = setTimeout(() => {
      const sidebarElement = document.querySelector('.companies-container .unified-sidebar-content');
      if (sidebarElement) {
        scrollElement = sidebarElement as HTMLElement;
        scrollElement.addEventListener('scroll', handleScroll, { passive: true });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  
  // Extract unique countries and industries
  const allCountries = Array.from(new Set(
    companies.flatMap(company => company.locations.map((location: CompanyLocation) => location.country))
  )).sort();
  
  const allIndustries = Array.from(new Set(
    companies.map(company => company.industry)
  )).sort();
  
  // Filter companies based on filters
  const filteredCompanies = companies.filter(company => {
    // Search term filter
    const matchesSearch = searchTerm === '' || 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Country filter
    const matchesCountry = selectedCountries.length === 0 ||
      company.locations.some((location: CompanyLocation) => selectedCountries.includes(location.country));
    
    // Industry filter
    const matchesIndustry = selectedIndustries.length === 0 ||
      selectedIndustries.includes(company.industry);
    
    // Rating filter
    const matchesRating = company.globalRating >= minRating;
    
    // Top employer filter
    const matchesTopEmployer = (() => {
      switch (topEmployerFilter) {
        case 'top-rated': return company.globalRating >= 4.5;
        case 'excellent': return company.globalRating >= 4.8;
        case 'by-industry': 
          // Get companies by industry and return top 3 per industry
          const industryCompanies = companies.filter(c => c.industry === company.industry);
          const topIndustryCompanies = industryCompanies
            .sort((a, b) => b.globalRating - a.globalRating)
            .slice(0, 3);
          return topIndustryCompanies.includes(company);
        case 'highest-income':
          // Sort by highest average salary across all locations
          const allCompaniesWithSalary = companies.map(c => ({
            company: c,
            avgSalary: c.locations.reduce((sum, loc) => 
              sum + (loc.salaryRange.min + loc.salaryRange.max) / 2, 0) / c.locations.length
          }));
          const topSalaryCompanies = allCompaniesWithSalary
            .sort((a, b) => b.avgSalary - a.avgSalary)
            .slice(0, 10)
            .map(item => item.company);
          return topSalaryCompanies.includes(company);
        default: return true;
      }
    })();
    
    return matchesSearch && matchesCountry && matchesIndustry && matchesRating && matchesTopEmployer;
  });
  
  // Sort companies
  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    let comparison = 0;
    
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'rating') {
      comparison = a.globalRating - b.globalRating;
    } else if (sortBy === 'size') {
      // Sort by size category (small, medium, large)
      const sizeOrder = { small: 1, medium: 2, large: 3 };
      comparison = (sizeOrder[a.size as keyof typeof sizeOrder] || 0) - 
                  (sizeOrder[b.size as keyof typeof sizeOrder] || 0);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  
  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCountries([]);
    setSelectedIndustries([]);
    setMinRating(0);
    setSortBy('name');
    setSortOrder('asc');
    setTopEmployerFilter('all');
  };
  
  // Handle showing company location on map
  const highlightCompanyOnMap = (company: Company) => {
    if (mapContext && company.locations.length > 0) {
      const primaryLocation = company.locations[0];
      mapContext.highlightLocation(primaryLocation);
    }
  };

  // Handle referral click
  const handleReferralClick = async (e: React.MouseEvent, companyId: string, companyName: string) => {
    e.stopPropagation(); // Prevent card click
    if (!isAuthenticated || !user?.id) {
      alert('Please log in to refer yourself to companies');
      return;
    }
    
    const isCurrentlyReferred = referrals.has(companyId);
    
    if (isCurrentlyReferred) {
      // For now, we don't allow removing referrals
      alert('You have already expressed interest in this company');
      return;
    }
    
    try {
      await referralsService.submitReferral(user.id, companyId, companyName);
      setReferrals(prev => {
        const newReferrals = new Set(prev);
        newReferrals.add(companyId);
        return newReferrals;
      });
      alert(`Successfully expressed interest in ${companyName}!`);
    } catch (error) {
      alert('Failed to submit referral. Please try again.');
      // console.error('Error submitting referral:', error);
    }
  };
  
  return (
    <div className="unified-sidebar companies-container">
      <div className="sidebar-header">
        <h2><span className="material-icons">business</span> Company Directory</h2>
        <p>Explore top employers and opportunities</p>
      </div>
      
      <div className="unified-sidebar-content">
      <div className="unified-content-section results-stats">
        <span className="stat-item">{sortedCompanies.length} companies</span>
        <span className="stat-item">{companies.filter(c => c.globalRating >= 4.5).length} top rated</span>
      </div>
      
      {/* Search box */}
      <div className="unified-content-section search-container">
        <input 
          type="text"
          className="search-input"
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {/* Filters section */}
      <div className="unified-content-section filters-container">
        <div className="section-header">
          <h3>Filters</h3>
          <button 
            className="toggle-collapse-btn"
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            aria-label={isFiltersCollapsed ? "Expand filters" : "Collapse filters"}
          >
            <span className="material-icons">
              {isFiltersCollapsed ? 'expand_more' : 'expand_less'}
            </span>
          </button>
        </div>
        {!isFiltersCollapsed && (
          <>
        {/* Top Employers Quick Filters */}
        <div className="filter-group">
          <label>Top Employers</label>
          <div className="top-employer-buttons">
            <button 
              className={`top-employer-btn ${topEmployerFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTopEmployerFilter('all')}
            >
              All Companies
            </button>
            <button 
              className={`top-employer-btn ${topEmployerFilter === 'top-rated' ? 'active' : ''}`}
              onClick={() => setTopEmployerFilter('top-rated')}
            >
              Top Rated (4.5+)
            </button>
            <button 
              className={`top-employer-btn ${topEmployerFilter === 'excellent' ? 'active' : ''}`}
              onClick={() => setTopEmployerFilter('excellent')}
            >
              Excellent (4.8+)
            </button>
            <button 
              className={`top-employer-btn ${topEmployerFilter === 'by-industry' ? 'active' : ''}`}
              onClick={() => setTopEmployerFilter('by-industry')}
            >
              Top by Industry
            </button>
            <button 
              className={`top-employer-btn ${topEmployerFilter === 'highest-income' ? 'active' : ''}`}
              onClick={() => setTopEmployerFilter('highest-income')}
            >
              Highest Income
            </button>
          </div>
        </div>

        {/* Countries multi-select */}
        <MultiSelect 
          label="Countries"
          options={allCountries}
          selected={selectedCountries}
          onChange={setSelectedCountries}
          placeholder="All Countries"
          searchPlaceholder="Search countries..."
        />
        
        {/* Industries multi-select */}
        <MultiSelect 
          label="Industries"
          options={allIndustries}
          selected={selectedIndustries}
          onChange={setSelectedIndustries}
          placeholder="All Industries"
          searchPlaceholder="Search industries..."
        />
        
        {/* Min Rating slider */}
        <div className="filter-group">
          <label>Minimum Rating</label>
          <div className="rating-slider">
            <input 
              type="range"
              min="0"
              max="5"
              step="0.5"
              value={minRating}
              onChange={(e) => setMinRating(parseFloat(e.target.value))}
            />
            <span className="rating-value">{minRating}+</span>
          </div>
        </div>
        
        {/* Sort by */}
        <div className="filter-group">
          <label>Sort By</label>
          <div className="sort-controls">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="name">Name</option>
              <option value="rating">Rating</option>
              <option value="size">Size</option>
            </select>
            <button 
              className="sort-direction"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              aria-label={sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        
        <button 
          className="reset-filters-btn"
          onClick={resetFilters}
        >
          Reset Filters
        </button>
        </>
        )}
      </div>
      
      {/* Results Statistics */}
      {sortedCompanies.length > 0 && (
        <div className="unified-content-section results-statistics">
          <div className="section-header">
            <h3>Results Summary</h3>
            <button 
              className="toggle-collapse-btn"
              onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
              aria-label={isStatsCollapsed ? "Expand statistics" : "Collapse statistics"}
            >
              <span className="material-icons">
                insights
              </span>
            </button>
          </div>
          {!isStatsCollapsed && (
            <>
          <div className="stat-item">
            <span className="stat-label">Companies Found:</span>
            <span className="stat-value">{sortedCompanies.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Average Rating:</span>
            <span className="stat-value">
              {(sortedCompanies.reduce((sum, company) => sum + company.globalRating, 0) / sortedCompanies.length).toFixed(1)}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Excellent (4.5+):</span>
            <span className="stat-value">
              {sortedCompanies.filter(c => c.globalRating >= 4.5).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Good (4.0-4.5):</span>
            <span className="stat-value">
              {sortedCompanies.filter(c => c.globalRating >= 4.0 && c.globalRating < 4.5).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Fair (3.5-4.0):</span>
            <span className="stat-value">
              {sortedCompanies.filter(c => c.globalRating >= 3.5 && c.globalRating < 4.0).length}
            </span>
          </div>
          </>
          )}
        </div>
      )}
      
      {/* Companies list */}
      <div className="unified-content-section companies-list">
        {sortedCompanies.length === 0 ? (
          <div className="no-results">No companies match your filters</div>
        ) : (
          sortedCompanies.map(company => {
            // Create location text
            const locationText = company.locations.map((location: CompanyLocation) => 
              `${location.city}, ${location.country}`
            ).join(' • ');
            
            return (
              <div 
                key={company.id} 
                className="company-card"
                onClick={() => {
                  highlightCompanyOnMap(company);
                  onCompanySelect?.(company);
                }}
              >
                <div className="company-header">
                  <h3 className="company-name">{company.name}</h3>
                  <div className="company-header-right">
                    <div className="company-rating">
                      <span className="rating-value">{company.globalRating.toFixed(1)}</span>
                      <span className="rating-stars">★</span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ComparisonButton type="company" item={company} className="compact" />
                    </div>
                  </div>
                </div>
                
                <div className="company-meta">
                  <div className="company-industry">{company.industry}</div>
                  <div className="company-size">{company.size}</div>
                </div>
                
                <div className="company-founded">Founded {company.foundedYear}</div>
                <div className="company-description">{company.description}</div>
                
                <div className="company-locations">
                  <strong>Locations:</strong> {locationText}
                </div>
                
                <div className="company-tags">
                  {company.tags.slice(0, 4).map((tag: string, index: number) => (
                    <span key={index} className="company-tag">{tag}</span>
                  ))}
                  {company.tags.length > 4 && (
                    <span className="company-tag more-tag">+{company.tags.length - 4}</span>
                  )}
                </div>
                
                <div className="company-actions">
                  <button 
                    className={`referral-btn ${referrals.has(company.id) ? 'active' : ''}`}
                    onClick={(e) => handleReferralClick(e, company.id, company.name)}
                    title={referrals.has(company.id) ? 'Already referred' : 'I want to work here'}
                    disabled={isLoadingReferrals}
                  >
                    {referrals.has(company.id) ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 3.5L6 11l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Referred
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        I Want to Work Here
                      </>
                    )}
                  </button>
                  {onViewReferralDashboard && (
                    <button 
                      className="dashboard-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewReferralDashboard(company.id, company.name);
                      }}
                      title="View referral dashboard"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M1 3h14M1 8h14M1 13h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Referral Dashboard
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
};

export default Companies;